import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { SessionService } from './session.service';
import { WidgetTokenService } from './widget-token.service';

/** A typed item from the chat stream: answer text or a tool-call / approval notification. */
export type ChatStreamEvent =
  | { kind: 'text'; delta: string }
  | { kind: 'tool_call_start'; id: string; displayText: string }
  | { kind: 'tool_call_end'; id: string; status: 'ok' | 'error' }
  | { kind: 'tool_budget_exhausted' }
  | {
      kind: 'action_approval_required';
      id: string;
      actionName: string;
      displayName: string;
      actionKind: 'Read' | 'Write';
      args: { name: string; value: string }[];
    }
  | { kind: 'action_decision_expired'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'answer_meta'; messageId: string }
  | { kind: 'sources'; items: AnswerSource[] }
  | { kind: 'follow_ups'; items: string[] }
  | { kind: 'done' };

/**
 * A web page an answer drew on. Only website sources are ever shown: an uploaded file's name is the
 * tenant's internal detail, so a source without a public address never reaches the visitor.
 */
export interface AnswerSource {
  title: string;
  url: string;
}

export type FeedbackRating = 'up' | 'down' | null;

/**
 * Named events this build understands beyond the original set, sent with every stream request so
 * the engine only emits them to a widget that can read them. An older widget would otherwise print
 * an unknown event's JSON into the answer; the engine also ignores the field if it predates it.
 */
const STREAM_EVENTS = ['answer_meta', 'sources', 'follow_ups'];

const MAX_SOURCES = 3;
const MAX_FOLLOW_UPS = 3;

/** The address as a normalised http(s) URL, or null for anything else. */
function safeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Website sources only. The engine sends nothing else, and this drops anything without an http(s)
 * address all the same, so a file name can't reach the visitor even from an engine that did.
 */
function parseSources(data: any): AnswerSource[] {
  const items = Array.isArray(data?.items) ? data.items : [];
  return items
    .map((s: any) => ({ title: typeof s?.title === 'string' ? s.title.trim() : '', url: safeUrl(s?.url) }))
    .filter((s: { title: string; url: string | null }): s is AnswerSource => !!s.title && !!s.url)
    .slice(0, MAX_SOURCES);
}

function parseFollowUps(data: any): string[] {
  const items = Array.isArray(data?.items) ? data.items : [];
  return items
    .filter((q: unknown): q is string => typeof q === 'string' && q.trim().length > 0)
    .map((q: string) => q.trim())
    .slice(0, MAX_FOLLOW_UPS);
}

/**
 * The chat endpoints' query: the project, and the widget when the frame knows it. The widget token
 * already names the widget; the query is what a request without a token is attributed by.
 */
function chatQuery(projectId: string, widgetId?: string | null): string {
  const project = `projectId=${encodeURIComponent(projectId)}`;
  return widgetId ? `${project}&widgetId=${encodeURIComponent(widgetId)}` : project;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  constructor() { }

  private http = inject(HttpClient);
  private tokens = inject(WidgetTokenService);
  private session = inject(SessionService);

  sendMessage(question: string, projectId: string, widgetId?: string | null): Observable<any> {
    const url = `${environment.chatApiUrl}/Chat/ask?${chatQuery(projectId, widgetId)}`;
    return this.http.post(url, { question });
  }

  sendMessageStream(question: string, projectId: string, widgetId?: string | null): Observable<ChatStreamEvent> {
    const url = `${environment.chatApiUrl}/Chat/ask/stream?${chatQuery(projectId, widgetId)}`;
    return this.streamSse(url, { question, events: STREAM_EVENTS });
  }

  /**
   * Rates one answer, by the id its `answer_meta` event carried. `null` takes the rating back.
   * Goes through HttpClient, so the session and widget-token headers are added by the interceptor.
   */
  sendFeedback(messageId: string, rating: FeedbackRating, projectId: string, widgetId?: string | null): Observable<unknown> {
    const url = `${environment.chatApiUrl}/Chat/feedback?${chatQuery(projectId, widgetId)}`;
    return this.http.post(url, { messageId, rating });
  }

  /**
   * Resumes the conversation after the user approves or rejects a pending HTTP Action.
   * Streams the model's continuation over the same SSE contract as `sendMessageStream`.
   */
  sendActionDecisionStream(
    approvalId: string,
    approved: boolean,
    projectId: string,
    widgetId?: string | null
  ): Observable<ChatStreamEvent> {
    const url = `${environment.chatApiUrl}/Chat/action/decision/stream?${chatQuery(projectId, widgetId)}`;
    return this.streamSse(url, { approvalId, approved, events: STREAM_EVENTS });
  }

  private streamSse(url: string, body: unknown): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>((observer) => {
      const controller = new AbortController();

      const buildHeaders = (): Record<string, string> => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        };

        const sessionId = this.session.id;
        if (sessionId) headers['X-Session-Id'] = sessionId;

        const widgetToken = this.tokens.token;
        if (widgetToken) headers['X-Widget-Token'] = widgetToken;

        return headers;
      };

      const post = () => fetch(url, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(body),
        signal: controller.signal
      });

      // This path uses fetch directly for the streaming body, so the HTTP interceptor's token
      // renewal does not apply to it. Renewing here too keeps an expired token from silently
      // costing the visitor the message they just sent — the case most likely to hit an expiry,
      // since it is the one that happens after a long idle tab.
      post().then(async (first) => {
        let res = first;

        if (res.status === 403) {
          const renewed = await firstValueFrom(this.tokens.renew()).catch(() => null);
          if (renewed) res = await post();
        }

        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

        this.session.store(res.headers.get('x-session-id'));

        if (!res.body) throw new Error('No response body');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let currentEvent = 'message';
        let dataLines: string[] = [];

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let idx: number;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);

            const lines = chunk.replace(/\r/g, '').split('\n');
            for (const line of lines) {
              if (line.startsWith('event:')) {
                currentEvent = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                dataLines.push(line.replace(/^data:\s?/, ''));
              }
            }

            const payload = dataLines.join('\n');
            dataLines = [];

            if (currentEvent === 'done' || payload === '[DONE]') {
              observer.next({ kind: 'done' });
              observer.complete();
              return;
            }

            const structuredEvents = new Set([
              'tool_call_start',
              'tool_call_end',
              'tool_budget_exhausted',
              'action_approval_required',
              'action_decision_expired',
              'error',
              'answer_meta',
              'sources',
              'follow_ups'
            ]);

            if (structuredEvents.has(currentEvent)) {
              try {
                const data = payload ? JSON.parse(payload) : {};
                switch (currentEvent) {
                  case 'tool_call_start':
                    observer.next({ kind: 'tool_call_start', id: data.id, displayText: data.displayText });
                    break;
                  case 'tool_call_end':
                    observer.next({ kind: 'tool_call_end', id: data.id, status: data.status });
                    break;
                  case 'tool_budget_exhausted':
                    observer.next({ kind: 'tool_budget_exhausted' });
                    break;
                  case 'action_approval_required':
                    observer.next({
                      kind: 'action_approval_required',
                      id: data.id,
                      actionName: data.actionName,
                      displayName: data.displayName ?? data.actionName,
                      actionKind: data.kind === 'Write' ? 'Write' : 'Read',
                      args: Array.isArray(data.args) ? data.args : []
                    });
                    break;
                  case 'action_decision_expired':
                    observer.next({ kind: 'action_decision_expired', message: data.message ?? 'Approval expired.' });
                    break;
                  case 'error':
                    observer.next({ kind: 'error', message: data.message ?? 'An error occurred.' });
                    break;
                  case 'answer_meta':
                    if (typeof data.messageId === 'string' && data.messageId) {
                      observer.next({ kind: 'answer_meta', messageId: data.messageId });
                    }
                    break;
                  case 'sources':
                    observer.next({ kind: 'sources', items: parseSources(data) });
                    break;
                  case 'follow_ups':
                    observer.next({ kind: 'follow_ups', items: parseFollowUps(data) });
                    break;
                }
              } catch {
                /* ignore malformed structured event */
              }
            } else if (currentEvent !== 'message') {
              // A named event from a newer engine. Never answer text — dropping it is the only
              // safe reading.
            } else if (payload) {
              observer.next({ kind: 'text', delta: payload });
            }
            currentEvent = 'message';
          }
        }

        observer.complete();
      }).catch(err => {
        if (err?.name !== 'AbortError') observer.error(err);
      });

      return () => {
        controller.abort();
      };
    });
  }

}
